import { useRef, useState } from "react";
import { useAuth } from "../modules/auth/AuthContext";
import { apiBaseUrl, uploadMyProfilePhoto } from "../lib/api";

function ProfileIcon({ name }: { name: "user" | "shield" | "mail" | "camera" | "id" | "badge" }) {
  const paths = {
    user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    shield: <path d="M12 3 5 6v5c0 5 3 8 7 10 4-2 7-5 7-10V6z" />,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    camera: <><path d="M7 7h2l1.5-2h3L15 7h2a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3Z" /><circle cx="12" cy="13" r="3" /></>,
    id: <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="12" r="2.5" /><path d="M14 10h5M14 14h3" /></>,
    badge: <><path d="M12 3 4 7v6c0 4.5 3.5 8 8 8s8-3.5 8-8V7z" /><path d="m9 12 2 2 4-4" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

export function ProfilePage() {
  const { user, updateProfilePhoto } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoSuccess, setPhotoSuccess] = useState(false);

  const fullName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || "Coach";
  const initials = fullName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const profilePhotoSrc = user?.profile_photo_url ? `${apiBaseUrl}${user.profile_photo_url}` : null;

  async function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setPhotoError("El archivo debe ser una imagen.");
      setPhotoSuccess(false);
      return;
    }
    setPhotoError(null);
    setPhotoSuccess(false);
    setUploadingPhoto(true);
    try {
      const { profile_photo_url } = await uploadMyProfilePhoto(file);
      updateProfilePhoto(profile_photo_url);
      setPhotoSuccess(true);
      setTimeout(() => setPhotoSuccess(false), 2500);
    } catch {
      setPhotoError("No se pudo subir la foto. Intenta nuevamente.");
    } finally {
      setUploadingPhoto(false);
    }
  }

  return (
    <section className="profile-page">
      <header className="profile-hero">
        <span className="profile-hero-avatar">
          {profilePhotoSrc ? (
            <img src={profilePhotoSrc} alt={`Foto de perfil de ${fullName}`} />
          ) : (
            initials
          )}
        </span>
        <div className="profile-hero-body">
          <p className="profile-hero-eyebrow">Mi perfil</p>
          <h1>{fullName}</h1>
          <p className="profile-hero-sub">{user?.email || "Sin correo registrado"}</p>
          <div className="profile-hero-actions">
            <button
              type="button"
              className="profile-photo-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
            >
              <ProfileIcon name="camera" /> {uploadingPhoto ? "Subiendo..." : "Cambiar foto"}
            </button>
            <span className={`profile-role-chip profile-role-chip--${(user?.role || "ADMIN").toLowerCase()}`}>
              <ProfileIcon name="badge" /> {user?.role || "ADMIN"}
            </span>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handlePhotoChange}
          />
          {photoError ? <small className="profile-photo-msg profile-photo-msg--error">{photoError}</small> : null}
          {photoSuccess ? <small className="profile-photo-msg profile-photo-msg--success">Foto actualizada ✓</small> : null}
        </div>
      </header>

      <article className="profile-info-card">
        <h2><ProfileIcon name="user" /> Datos de la cuenta</h2>
        <div className="profile-info-grid">
          <div className="profile-info-row">
            <span className="profile-info-label"><ProfileIcon name="user" /> Nombre completo</span>
            <strong>{fullName}</strong>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label"><ProfileIcon name="mail" /> Correo electrónico</span>
            <strong>{user?.email || "—"}</strong>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label"><ProfileIcon name="shield" /> Rol</span>
            <strong>{user?.role === "ADMIN" ? "Administrador" : "Coach"}</strong>
          </div>
          <div className="profile-info-row">
            <span className="profile-info-label"><ProfileIcon name="id" /> ID de usuario</span>
            <strong className="profile-info-mono">{user?.id || "—"}</strong>
          </div>
        </div>
        <small className="profile-info-note">
          Los datos de tu cuenta los gestiona el administrador del sistema.
        </small>
      </article>
    </section>
  );
}
